import { auxiliaryStorage } from "./auxiliaryStorage";
export type BgmState = "lobby" | "play" | "rest";
export type Cue = "tap" | "toggle" | "open" | "count" | "go" | "lock" | "reveal" | "score" | "whiz" | "bounce" | "boing" | "pass" | "rest" | "warning" | "blocked" | "join" | "leave" | "reconnect" | "error" | "complete";

export const cuePaths: Record<Cue, string> = {
  tap: "/audio/ui/tap.ogg", toggle: "/audio/ui/toggle.ogg", open: "/audio/ui/open.ogg",
  count: "/audio/game/count.ogg", go: "/audio/game/go.ogg", lock: "/audio/game/lock.ogg",
  reveal: "/audio/game/reveal.ogg", score: "/audio/game/score.ogg",
  whiz: "/audio/game/whiz.ogg", bounce: "/audio/game/bounce.ogg", boing: "/audio/game/boing.ogg",
  pass: "/audio/game/pass.ogg", rest: "/audio/game/rest.ogg",
  warning: "/audio/ui/warning.ogg", blocked: "/audio/ui/blocked.ogg",
  join: "/audio/room/join.ogg", leave: "/audio/room/leave.ogg", reconnect: "/audio/room/reconnect.ogg",
  error: "/audio/ui/error.ogg", complete: "/audio/game/complete.ogg",
};

export const bgmPaths: Record<BgmState, string> = {
  lobby: "/audio/portfolio-theme.mp3",
  play: "/audio/bgm/play.ogg",
  rest: "/audio/bgm/rest.ogg",
};

type AudioLike = Pick<HTMLAudioElement, "src" | "volume" | "loop" | "preload" | "paused" | "play" | "pause">;
type AudioRuntime = {
  storage: Pick<Storage, "getItem" | "setItem">;
  createAudio(src: string): AudioLike;
  now(): number;
  isHidden(): boolean;
  onFailure?(detail: { assetId: string; path: string; reason: "decode_or_play" | "resume" }): void;
};

const browserRuntime = (): AudioRuntime => typeof window === "undefined" ? {
  storage: { getItem: () => null, setItem: () => undefined },
  createAudio: (src) => ({ src, volume: 0, loop: false, preload: "none", paused: true, play: async () => undefined, pause: () => undefined }),
  now: () => Date.now(), isHidden: () => false,
} : {
  storage: auxiliaryStorage,
  createAudio: (src) => new Audio(src),
  now: () => performance.now(),
  isHidden: () => document.hidden,
  onFailure: (detail) => window.dispatchEvent(new CustomEvent("hs-audio-failure", { detail })),
};

function clampVolume(value: string | null, fallback: number) {
  if (value === null || value.trim() === "") return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : fallback;
}

export class AudioBus {
  private bgm: AudioLike | null = null;
  private state: BgmState | null = null;
  private unlocked = false;
  private lastCueAt = new Map<Cue, number>();
  constructor(private readonly runtime: AudioRuntime) {}

  unlock() { this.unlocked = true; }
  isUnlocked() { return this.unlocked; }
  private enabled() { return this.runtime.storage.getItem("hs-mute") !== "on" && this.runtime.storage.getItem("hs-reduced-sensory") !== "on"; }
  private effectVolume() { return clampVolume(this.runtime.storage.getItem("hs-audio-volume"), 0.4); }
  private musicVolume() { return Math.min(0.35, this.effectVolume()); }

  setMuted(muted: boolean) { this.runtime.storage.setItem("hs-mute", muted ? "on" : "off"); this.refresh(); }
  setReducedSensory(reduced: boolean) { this.runtime.storage.setItem("hs-reduced-sensory", reduced ? "on" : "off"); this.refresh(); }
  setVolume(volume: number) { this.runtime.storage.setItem("hs-audio-volume", String(Math.min(1, Math.max(0, volume)))); if (this.bgm) this.bgm.volume = this.musicVolume(); }

  play(cue: Cue) {
    if (!this.enabled() || !this.unlocked || this.runtime.isHidden()) return false;
    const now = this.runtime.now();
    if (now - (this.lastCueAt.get(cue) ?? -Infinity) < 150) return false;
    this.lastCueAt.set(cue, now);
    const audio = this.runtime.createAudio(cuePaths[cue]);
    audio.volume = this.effectVolume();
    void audio.play().catch(() => this.runtime.onFailure?.({ assetId: `sfx-${cue}`, path: cuePaths[cue], reason: "decode_or_play" }));
    return true;
  }

  setBgm(state: BgmState) {
    if (!this.enabled() || !this.unlocked || this.runtime.isHidden() || this.state === state) return false;
    this.bgm?.pause();
    const path = bgmPaths[state];
    const audio = this.runtime.createAudio(path);
    audio.loop = true; audio.volume = this.musicVolume(); audio.preload = "none";
    this.bgm = audio; this.state = state;
    void audio.play().catch(() => this.runtime.onFailure?.({ assetId: `bgm-${state}`, path, reason: "decode_or_play" }));
    return true;
  }

  refresh() {
    if (!this.enabled()) { this.suspend(); return; }
    if (this.state && this.unlocked && !this.runtime.isHidden()) {
      const state = this.state; this.state = null; this.setBgm(state);
    }
  }
  suspend() { this.bgm?.pause(); }
  resume() {
    if (!this.enabled() || !this.unlocked || this.runtime.isHidden() || !this.bgm) return false;
    void this.bgm.play().catch(() => this.runtime.onFailure?.({ assetId: `bgm-${this.state ?? "unknown"}`, path: this.bgm?.src ?? "", reason: "resume" }));
    return true;
  }
}

export const audioBus = new AudioBus(browserRuntime());

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => document.hidden ? audioBus.suspend() : audioBus.resume());
  window.addEventListener("pagehide", () => audioBus.suspend());
  window.addEventListener("pageshow", () => audioBus.resume());
}
